import { isAuthenticated } from '@app/modules/routes/guards';
import { UserInfo, UserUtils } from '@app/modules/userinfo';
import { ApiDataResponse } from '@app/types/dto';
import { CheckAccessRequest, CheckAccessResponse, UserLoginInfo, UserRole } from '@app/types/user';
import { apiCall, ApiResponse } from './api';
import { AUTH_ENDPOINTS, USER_ENDPOINTS } from './endpointUrl';

/**
 * Error Response Model
 */
export interface ErrorResponse {
  field?: string;
  messageCode: string;
  messageJp: string;
  messageEn: string;
}

/**
 * SSO Finalize Response Data
 * Returned by /api/auth/sso-finalize after exchanging session_token for HttpOnly Cookie
 */
export interface SsoFinalizeData {
  userId: string;
  email: string;
  fullName: string;
  sessionStatus: string;
}

/**
 * Authenticate API Service
 */
export const authApiService = {
  /**
   * Get all user in a system
   * GET /api/users
   */
  getAllUsers: (): Promise<any> => {
    return apiCall<any>(USER_ENDPOINTS.GET_ALL_USERS);
  },

  /**
   * Get user info by userId
   * GET /api/users/{userId}
   */
  getUserById: (userId: string): Promise<any> => {
    return apiCall<any>(USER_ENDPOINTS.GET_USER_BY_ID(userId));
  },

  /**
   * Login local user
   * POST /api/auth/login
   */
  loginLocal: (payload: { username: string }): Promise<ApiDataResponse<UserLoginInfo>> => {
    return apiCall<ApiDataResponse<UserLoginInfo>>(AUTH_ENDPOINTS.LOGIN, {
      method: 'POST',
      data: { ...payload, loginType: 'LOCAL' },
    }).catch((error) => {
      const errorData = error.response?.data as ErrorResponse;
      throw errorData;
    });
  },

  /**
   * Get current user info
   * GET /api/auth/user-info
   */
  getUserInfo: (): Promise<ApiResponse<UserInfo>> => {
    return apiCall<ApiResponse<UserInfo>>(AUTH_ENDPOINTS.USER_INFO);
  },

  /**
   * Get current user role and permissions
   * GET /api/auth/userrole
   */
  getUserRole: (): Promise<UserRole> => {
    return apiCall<UserRole>(AUTH_ENDPOINTS.USER_ROLE);
  },

  /**
   * Logout — invalidates session/cookie on the server.
   * POST /api/auth/logout
   */
  logout: (): Promise<any> => {
    return apiCall<any>(AUTH_ENDPOINTS.LOGOUT, { method: 'POST' });
  },

  /**
   * Exchange session_token (from SSO callback URL) for an HttpOnly session cookie.
   * GET /api/auth/sso-finalize?session_token=<id>
   */
  ssoFinalize: (sessionToken: string): Promise<ApiDataResponse<SsoFinalizeData>> => {
    return apiCall<ApiDataResponse<SsoFinalizeData>>(
      `${AUTH_ENDPOINTS.SSO_FINALIZE}?session_token=${encodeURIComponent(sessionToken)}`
    );
  },

  /**
   * API-SC001-3: This API is used to check whether the current user has access to a particular screen or function.
   * POST /api/auth/check-access
   */
  checkAccess: (payload: CheckAccessRequest): Promise<CheckAccessResponse> => {
    return apiCall<CheckAccessResponse>(AUTH_ENDPOINTS.CHECK_ACCESS, {
      method: 'POST',
      data: payload,
    });
  },

  /**
   * Check if user is authenticated by verifying session on the server.
   * This is used for validating the session cookie, especially after SSO login or page refresh.
   * GET /api/auth/check
   * @returns
   */
  checkAuthentication: async (): Promise<any> => {
    return await apiCall<any>(AUTH_ENDPOINTS.CHECK_AUTHENTICATION);
  },
};
